import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Search, Download, ChevronLeft, ChevronRight, CheckCircle2, Circle } from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';

const fmtDate = v => v ? v.slice(0, 10) : '—';

const STEPS = [
  { key: 'has_dob',               label: 'Date of Birth' },
  { key: 'has_address',           label: 'Address' },
  { key: 'has_phone',             label: 'Phone' },
  { key: 'has_emergency_contact', label: 'Emergency Contact' },
  { key: 'has_start_date',        label: 'Start Date' },
  { key: 'has_qualifications',    label: 'Qualifications' },
  { key: 'has_documents',         label: 'Documents' },
  { key: 'has_pay_group',         label: 'Pay Group' },
];

const STATUS_BADGE = {
  complete:     'bg-emerald-100 text-emerald-700',
  in_progress:  'bg-amber-100 text-amber-700',
  not_started:  'bg-slate-100 text-slate-500',
};

export default function StaffOnboarding() {
  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch]   = useState('');
  const [status, setStatus]   = useState('all');
  const [page, setPage]       = useState(1);
  const limit = 25;

  const load = useCallback(() => {
    setLoading(true);
    const p = new URLSearchParams({ search, status });
    api.get(`/reports/staff-onboarding?${p}`)
      .then(r => setRows(r.data))
      .catch(() => toast.error('Failed to load onboarding data'))
      .finally(() => setLoading(false));
  }, [search, status]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [search, status]);

  const totalPages = Math.max(1, Math.ceil(rows.length / limit));
  const from = rows.length === 0 ? 0 : (page - 1) * limit + 1;
  const to   = Math.min(page * limit, rows.length);
  const pageData = rows.slice(from > 0 ? from - 1 : 0, to);

  const stats = {
    complete:    rows.filter(r => r.onboarding_status === 'complete').length,
    in_progress: rows.filter(r => r.onboarding_status === 'in_progress').length,
    not_started: rows.filter(r => r.onboarding_status === 'not_started').length,
  };

  function downloadCSV() {
    if (!rows.length) return;
    const cols = ['Staff Member', 'Role', 'Email', 'Joined', 'Status', 'Completion %', ...STEPS.map(s => s.label)];
    const data = rows.map(r => [r.staff_name, r.role, r.email || '', fmtDate(r.joined_date), r.onboarding_status, r.completion_pct, ...STEPS.map(s => r[s.key] ? 'Yes' : 'No')]);
    const csv  = [cols, ...data].map(r => r.join(',')).join('\n');
    const a = document.createElement('a'); a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    a.download = `staff_onboarding_${new Date().toISOString().slice(0,10)}.csv`; a.click();
  }

  return (
    <div>
      {/* Summary tiles */}
      <div className="grid grid-cols-3 gap-3 px-4 pt-4">
        {[
          { label: 'Complete', key: 'complete', color: 'text-emerald-600' },
          { label: 'In Progress', key: 'in_progress', color: 'text-amber-600' },
          { label: 'Not Started', key: 'not_started', color: 'text-slate-500' },
        ].map(({ label, key, color }) => (
          <button key={key} onClick={() => setStatus(status === key ? 'all' : key)}
            className={`rounded-xl border p-3 text-left transition-all ${status === key ? 'ring-2 ring-blue-500 border-blue-300' : 'border-slate-200 hover:border-slate-300 bg-white'}`}>
            <div className="text-xs text-slate-500 mb-1">{label}</div>
            <div className={`text-2xl font-bold ${color}`}>{stats[key]}</div>
          </button>
        ))}
      </div>

      {/* Controls */}
      <div className="flex items-center gap-3 px-4 py-3 bg-white border-b border-slate-200 flex-wrap mt-3">
        <div className="relative">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input type="text" placeholder="Search staff..." value={search}
            onChange={e => setSearch(e.target.value)}
            className="border border-slate-300 rounded pl-8 pr-3 py-1.5 text-sm w-48 focus:outline-none focus:ring-2 focus:ring-blue-500" />
        </div>

        <select value={status} onChange={e => setStatus(e.target.value)}
          className="border border-slate-300 rounded px-3 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500">
          <option value="all">All Staff</option>
          <option value="complete">Complete</option>
          <option value="in_progress">In Progress</option>
          <option value="not_started">Not Started</option>
        </select>

        <div className="flex-1" />
        <span className="text-sm text-slate-500">{rows.length} staff member{rows.length !== 1 ? 's' : ''}</span>
        <button onClick={downloadCSV} className="p-1.5 border border-slate-300 rounded text-slate-500 hover:bg-slate-50" title="Download CSV">
          <Download size={15} />
        </button>
      </div>

      {/* Table */}
      <div className="bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200">
              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 whitespace-nowrap">Staff Member</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 whitespace-nowrap">Role</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 whitespace-nowrap">Joined</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 whitespace-nowrap">Completion</th>
              {STEPS.map(s => (
                <th key={s.key} className="px-3 py-3 text-center text-xs font-semibold text-slate-700 whitespace-nowrap">{s.label}</th>
              ))}
              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 whitespace-nowrap">Status</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={13} className="px-4 py-10 text-center text-slate-400">Loading…</td></tr>
            ) : !pageData.length ? (
              <tr><td colSpan={13} className="px-4 py-10 text-center text-slate-400">No records found</td></tr>
            ) : pageData.map((r, i) => (
              <tr key={r.id || i} className="border-b border-slate-100 hover:bg-slate-50/50">
                <td className="px-4 py-2.5 font-medium whitespace-nowrap">
                  <Link to={`/staff/${r.id}`} className="text-blue-600 hover:underline">{r.staff_name}</Link>
                </td>
                <td className="px-4 py-2.5 text-slate-500 text-xs capitalize">{r.role?.replace(/_/g, ' ')}</td>
                <td className="px-4 py-2.5 text-slate-500 whitespace-nowrap">{fmtDate(r.joined_date)}</td>
                <td className="px-4 py-2.5 whitespace-nowrap">
                  <div className="flex items-center gap-2">
                    <div className="w-16 bg-slate-100 rounded-full h-1.5">
                      <div className={`h-1.5 rounded-full ${r.completion_pct === 100 ? 'bg-emerald-500' : r.completion_pct >= 50 ? 'bg-amber-500' : 'bg-red-400'}`}
                        style={{ width: `${r.completion_pct}%` }} />
                    </div>
                    <span className="text-xs font-medium text-slate-700">{r.completion_pct}%</span>
                  </div>
                </td>
                {STEPS.map(s => (
                  <td key={s.key} className="px-3 py-2.5 text-center">
                    {r[s.key]
                      ? <CheckCircle2 size={15} className="text-emerald-500 mx-auto" />
                      : <Circle size={15} className="text-slate-300 mx-auto" />}
                  </td>
                ))}
                <td className="px-4 py-2.5 whitespace-nowrap">
                  <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium capitalize ${STATUS_BADGE[r.onboarding_status] || ''}`}>
                    {r.onboarding_status?.replace('_', ' ')}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between px-4 py-3 bg-white border-t border-slate-200 text-sm text-slate-600 flex-wrap gap-2">
        <span>Showing {from}–{to} of {rows.length} staff</span>
        <div className="flex items-center gap-1">
          <button onClick={() => setPage(p => Math.max(1, p-1))} disabled={page <= 1}
            className="p-1.5 rounded border border-slate-200 text-slate-500 hover:bg-slate-50 disabled:opacity-40">
            <ChevronLeft size={14} />
          </button>
          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
            const pg = totalPages <= 5 ? i + 1 : page <= 3 ? i + 1 : page + i - 2;
            if (pg > totalPages) return null;
            return (
              <button key={pg} onClick={() => setPage(pg)}
                className={`w-7 h-7 rounded text-xs font-medium border ${pg === page ? 'bg-blue-600 text-white border-blue-600' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}>
                {pg}
              </button>
            );
          })}
          <button onClick={() => setPage(p => Math.min(totalPages, p+1))} disabled={page >= totalPages}
            className="p-1.5 rounded border border-slate-200 text-slate-500 hover:bg-slate-50 disabled:opacity-40">
            <ChevronRight size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}
